import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Clock, Check, X, FileEdit, Send, Trash2, Ban } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { LeaveRequest } from '../../services/leaveRequestService';
import { leaveDayCount, leaveHoursDisplay, typeIcons, typeStyles } from './leave-request.utils';

interface LeaveRequestCardProps {
  request: LeaveRequest;
  onEdit?: (request: LeaveRequest) => void;
  onSubmit?: (request: LeaveRequest) => void;
  onDelete?: (request: LeaveRequest) => void;
  onCancel?: (request: LeaveRequest) => void;
  onRequestAmendment?: (request: LeaveRequest) => void;
  onRequestCancellation?: (request: LeaveRequest) => void;
  onApprove?: (request: LeaveRequest) => void;
  onReject?: (request: LeaveRequest) => void;
  showActions?: boolean;
  isApproverView?: boolean;
}

export const LeaveRequestCard: React.FC<LeaveRequestCardProps> = ({
  request,
  onEdit,
  onSubmit,
  onDelete,
  onCancel,
  onRequestAmendment,
  onRequestCancellation,
  onApprove,
  onReject,
  showActions = true,
  isApproverView = false,
}) => {
  const { t } = useTranslation();
  const TypeIcon = typeIcons[request.type];
  const typeStyle = typeStyles[request.type];
  const isApprovedRequest = request.state === 'approved';
  const isApprovedCancellation =
    isApprovedRequest &&
    (request.category === 'cancellation' || request.changeType === 'cancellation');

  const calculateDays = () => leaveDayCount(request);

  const getHoursDisplay = () => leaveHoursDisplay(request);

  const getDurationLabel = () => {
    if (request.durationType === 'hours') return t('leave.duration.hours', 'Hours');
    if (request.durationType === 'half_day_morning') return t('leave.duration.halfMorning', 'Half day (AM)');
    if (request.durationType === 'half_day_afternoon') return t('leave.duration.halfAfternoon', 'Half day (PM)');
    return calculateDays() === 1 ? t('leave.duration.oneDay', '1 day') : t('leave.duration.days', '{{count}} days', { count: calculateDays() });
  };

  const actionButtonClass = 'max-w-full justify-center';

  return (
    <Card className={cn(
      'transition-shadow hover:shadow-md',
      request.state === 'rejected' && 'border-red-200 dark:border-red-800',
      request.state === 'cancelled' && 'border-gray-200 dark:border-gray-700 opacity-75'
    )}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg',
            typeStyle.container,
          )}>
            <TypeIcon className={cn(
              'h-5 w-5',
              typeStyle.icon,
            )} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">
                {t(typeStyle.labelKey, typeStyle.defaultLabel)}
              </span>
              <Badge variant={request.state}>
                {t(`leave.state.${request.state}`, request.state)}
              </Badge>
            </div>

            <div className="text-sm text-muted-foreground mt-1">
              {format(new Date(request.startDate), 'MMM d, yyyy')}
              {request.startDate !== request.endDate && (
                <> - {format(new Date(request.endDate), 'MMM d, yyyy')}</>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {getDurationLabel()} ({getHoursDisplay()})
              </span>
            </div>

            {isApproverView && request.user && (
              <div className="text-sm mt-2">
                <span className="font-medium">{request.user.name}</span>
                {request.user.email && (
                  <span className="text-muted-foreground ml-2">
                    ({request.user.email})
                  </span>
                )}
              </div>
            )}

            {request.reason && (
              <p className="text-sm text-muted-foreground mt-2 italic">
                "{request.reason}"
              </p>
            )}

            {request.state === 'rejected' && request.rejectionReason && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                {t('leave.rejectedReason', 'Rejected')}: {request.rejectionReason}
              </p>
            )}

            {/* Actions */}
            {showActions && (
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                {request.state === 'draft' && (
                  <>
                    {onEdit && (
                      <Button variant="ghost" size="icon" aria-label={t('common.edit', 'Edit')} onClick={() => onEdit(request)}>
                        <FileEdit className="h-4 w-4" />
                      </Button>
                    )}
                    {onSubmit && (
                      <Button variant="ghost" size="icon" aria-label={t('common.submit', 'Submit')} onClick={() => onSubmit(request)}>
                        <Send className="h-4 w-4" />
                      </Button>
                    )}
                    {onDelete && (
                      <Button variant="ghost" size="icon" aria-label={t('common.delete', 'Delete')} onClick={() => onDelete(request)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </>
                )}

                {!isApproverView && request.state === 'pending' && onCancel && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onCancel(request)}
                    className={cn('text-gray-600 hover:text-gray-800', actionButtonClass)}
                  >
                    <Ban className="h-4 w-4 mr-1" />
                    {t('leave.cancel', 'Cancel')}
                  </Button>
                )}

                {!isApproverView && request.state === 'rejected' && request.changeType === 'amendment' && request.parentRequestId && onRequestCancellation && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRequestCancellation({ ...request, id: request.parentRequestId! })}
                    className={cn('text-red-600 hover:text-red-700', actionButtonClass)}
                  >
                    <Ban className="h-4 w-4 mr-1" />
                    {t('leave.requestCancellation', 'Request cancellation')}
                  </Button>
                )}

                {!isApproverView && isApprovedRequest && !isApprovedCancellation && (
                  <>
                    {onRequestAmendment && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRequestAmendment(request)}
                        className={actionButtonClass}
                      >
                        <FileEdit className="h-4 w-4 mr-1" />
                        {t('leave.requestAmendment', 'Request amendment')}
                      </Button>
                    )}
                    {onRequestCancellation && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRequestCancellation(request)}
                        className={cn('text-red-600 hover:text-red-700', actionButtonClass)}
                      >
                        <Ban className="h-4 w-4 mr-1" />
                        {t('leave.requestCancellation', 'Request cancellation')}
                      </Button>
                    )}
                  </>
                )}

                {isApproverView && request.state === 'pending' && (
                  <>
                    {onApprove && (
                      <Button
                        size="sm"
                        className={cn('bg-green-600 hover:bg-green-700', actionButtonClass)}
                        onClick={() => onApprove(request)}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        {t('leave.approve', 'Approve')}
                      </Button>
                    )}
                    {onReject && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => onReject(request)}
                        className={actionButtonClass}
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t('leave.reject', 'Reject')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
